var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import './BasicMap';
import '../form/ModeInput.js';
const DEFAULT_ZONE_COLOR = '#0078d4';
const ZONE_FILL_OPACITY = 0.35;
const ZONE_LINE_WIDTH = 2;
/**
 * Recursively walk any GeoJSON node (FeatureCollection, Feature, Geometry,
 * coordinate array) and invoke `fn` with each leaf `[lng, lat]` pair.
 */
function walkCoords(node, fn) {
    if (!node)
        return;
    if (node.type === 'FeatureCollection') {
        node.features?.forEach((f) => walkCoords(f?.geometry, fn));
        return;
    }
    if (node.type === 'Feature') {
        walkCoords(node.geometry, fn);
        return;
    }
    const coords = node.coordinates;
    if (coords)
        walkArr(coords, fn);
}
function walkArr(arr, fn) {
    if (!Array.isArray(arr))
        return;
    if (typeof arr[0] === 'number' && typeof arr[1] === 'number') {
        fn([arr[0], arr[1]]);
        return;
    }
    arr.forEach((x) => walkArr(x, fn));
}
const COLOR_CURRENT = '#F97316'; // orange-500 — focused study being edited
const COLOR_OTHERS = '#3B82F6'; // blue-500   — other studies in the universe
const COLOR_ATTRACTOR = '#10B981'; // emerald-500 — draws customers
const COLOR_COMPETITOR = '#EF4444'; // red-500   — competitive threat
const LAYER_LABELS = () => ({
    current: msg('Current study', { id: 'studyMap.layer.current', desc: 'Layer label: the study currently being edited' }),
    others: msg('Other studies', { id: 'studyMap.layer.others', desc: 'Layer label: other studies in the universe' }),
    attractors: msg('Attractors', { id: 'studyMap.layer.attractors', desc: 'Layer label: attractor networks' }),
    competitors: msg('Competitors', { id: 'studyMap.layer.competitors', desc: 'Layer label: competitor networks' }),
});
const LAYER_COLORS = {
    current: COLOR_CURRENT,
    others: COLOR_OTHERS,
    attractors: COLOR_ATTRACTOR,
    competitors: COLOR_COMPETITOR,
};
function escapeXmlAttr(s) {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}
/**
 * Fetch an image URL and return it as a base64 data URL. Browsers refuse to
 * load external images referenced inside an SVG that itself is served from a
 * data: URL (security restriction), so we inline the logo as base64.
 */
async function fetchAsDataUrl(url) {
    console.log('[StudyCreatorMap] fetchAsDataUrl ->', url);
    try {
        const response = await fetch(url, { mode: 'cors' });
        console.log('[StudyCreatorMap] fetchAsDataUrl response', url, 'status=', response.status, 'ok=', response.ok);
        if (!response.ok)
            return null;
        const blob = await response.blob();
        console.log('[StudyCreatorMap] fetchAsDataUrl blob', url, 'type=', blob.type, 'size=', blob.size);
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = typeof reader.result === 'string' ? reader.result : null;
                console.log('[StudyCreatorMap] fetchAsDataUrl encoded', url, 'len=', result?.length);
                resolve(result);
            };
            reader.onerror = () => {
                console.warn('[StudyCreatorMap] fetchAsDataUrl FileReader error', url);
                resolve(null);
            };
            reader.readAsDataURL(blob);
        });
    }
    catch (err) {
        console.warn('[StudyCreatorMap] fetchAsDataUrl FAILED', url, err);
        return null;
    }
}
function buildPinSvg(color, logoUrl) {
    if (logoUrl) {
        return `
    <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 22.5 30">
      <path style="fill:${color};" d="M11.25,0C5.04,0,0,5.04,0,11.25c0,2.68.91,5.28,2.59,7.37l8.66,11.38,8.66-11.38c1.67-2.09,2.59-4.69,2.59-7.37C22.5,5.04,17.46,0,11.25,0Z"/>
      <circle style="fill:#fff;" cx="11.25" cy="11.25" r="8"/>
      <clipPath id="logoClip"><circle cx="11.25" cy="11.25" r="7"/></clipPath>
      <image href="${escapeXmlAttr(logoUrl)}" x="4.25" y="4.25" width="14" height="14" clip-path="url(#logoClip)" preserveAspectRatio="xMidYMid slice"/>
    </svg>
    `;
    }
    return `
    <svg viewBox="0 0 16 16" width="100" height="100" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 0C4.686 0 2 2.686 2 6c0 1.43.486 2.816 1.379 3.932L8 16l4.621-6.068C13.514 8.816 14 7.43 14 6c0-3.314-2.686-6-6-6z" fill="${color}"/>
      <circle cx="8" cy="6" r="2" fill="#ffffff"/>
    </svg>
    `;
}
let StudyCreatorMap = class StudyCreatorMap extends LitElement {
    constructor() {
        super(...arguments);
        this.universeId = null;
        this.apiService = null;
        this.studyPoi = null;
        this.mapOptions = {};
        this.fullscreen = false;
        this.draggable = true;
        /** Zones to overlay on the map (step 2). Empty array = no overlay. */
        this.zones = [];
        /** zone_ids that should be hidden (via setLayoutProperty), not removed. */
        this.hiddenZoneIds = [];
        this.loading = false;
        this.ownPois = [];
        this.attractors = [];
        this.competitors = [];
        this.visibility = {
            current: true,
            others: true,
            attractors: true,
            competitors: true,
        };
        this.layerMenuOpen = false;
        this.mapInstance = null;
        this.mapLib = null;
        this.currentMarker = null;
        this.currentMarkerEl = null;
        this.addedSourceIds = new Set();
        this.addedLayerIds = new Set();
        this.addedImageIds = new Set();
        this.layerKindByLayerId = new Map();
        this.hasFlownToCurrent = false;
        // Zone overlay — kept separate from POI layers so we can swap them without
        // touching the rest of the map and update colors incrementally on change.
        this.zoneSourceIds = new Map();
        this.zoneFillLayerIds = new Map();
        this.zoneLineLayerIds = new Map();
        this.zoneColorByZoneId = new Map();
        this.zoneGeounitsCache = new Map();
        /** Tracks the overlay mode currently rendered on the map so we can detect
         *  'computed' → 'intersected' swaps (or vice versa) and rebuild geometry. */
        this.currentOverlayMode = undefined;
        /** Signature of the last fitBounds we issued so we don't rezoom on every
         *  color change or on no-op renders. */
        this.lastFitSignature = '';
        this.handleDocumentClick = (e) => {
            if (!this.layerMenuOpen)
                return;
            const path = e.composedPath();
            if (!path.includes(this)) {
                this.layerMenuOpen = false;
            }
        };
    }
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('click', this.handleDocumentClick);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this.handleDocumentClick);
    }
    // ─── Lifecycle ────────────────────────────────────────────────
    updated(changed) {
        console.log('[StudyCreatorMap] updated changed=', Array.from(changed.keys()), 'studyPoi.lat=', this.studyPoi?.latitude, 'studyPoi.lng=', this.studyPoi?.longitude);
        // Only re-fetch when the universe or service changes — NOT on every studyPoi tick
        if (changed.has('universeId') || changed.has('apiService')) {
            queueMicrotask(() => void this.loadData());
        }
        if (changed.has('studyPoi')) {
            this.renderCurrentMarker();
        }
        if (changed.has('ownPois') ||
            changed.has('attractors') ||
            changed.has('competitors') ||
            changed.has('studyPoi')) {
            this.renderAllLayers();
        }
        if (changed.has('visibility')) {
            this.applyVisibility();
        }
        if (changed.has('draggable') && this.currentMarker) {
            this.currentMarker.setDraggable(this.draggable);
            if (this.currentMarkerEl) {
                this.currentMarkerEl.style.cursor = this.draggable ? 'grab' : 'default';
            }
        }
        if (changed.has('zones') || changed.has('zoneOverlay')) {
            this.syncZoneOverlay();
        }
        if (changed.has('zones')) {
            // Fire-and-forget prefetch of intersected geometries so toggling to
            // 'intersected' is instant, even before the user clicks.
            this.prefetchZoneGeounits();
        }
        if (changed.has('hiddenZoneIds')) {
            this.applyZoneVisibility();
        }
    }
    async prefetchZoneGeounits() {
        if (!this.apiService?.getZoneGeounitsGeojson)
            return;
        if (!this.zones?.length)
            return;
        const toFetch = this.zones.filter((z) => !this.zoneGeounitsCache.has(z.zone_id));
        if (!toFetch.length)
            return;
        await Promise.all(toFetch.map(async (z) => {
            try {
                const fc = await this.apiService.getZoneGeounitsGeojson(z.zone_id);
                this.zoneGeounitsCache.set(z.zone_id, fc);
            }
            catch (e) {
                console.warn('[StudyCreatorMap] prefetch geounits failed', z.zone_id, e);
            }
        }));
    }
    async loadData() {
        if (!this.apiService || this.universeId == null) {
            this.ownPois = [];
            this.attractors = [];
            this.competitors = [];
            return;
        }
        this.loading = true;
        try {
            const links = await this.apiService.listUniversNetworks(this.universeId);
            const ownLinks = links.filter((l) => l.role === 'own');
            const competitorLinks = links.filter((l) => l.role === 'competitor');
            const attractorLinks = links.filter((l) => l.role === 'attractor');
            const [ownPois, competitors, attractors] = await Promise.all([
                Promise.all(ownLinks.map((l) => this.apiService.getPois(l.network_id))).then((chunks) => chunks.flat()),
                Promise.all(competitorLinks.map((l) => this.resolveBucket(l))),
                Promise.all(attractorLinks.map((l) => this.resolveBucket(l))),
            ]);
            this.ownPois = ownPois;
            this.competitors = competitors;
            this.attractors = attractors;
        }
        finally {
            this.loading = false;
        }
    }
    async resolveBucket(link) {
        console.log('[StudyCreatorMap] resolveBucket', link);
        const [pois, logo] = await Promise.all([
            this.apiService.getPois(link.network_id),
            link.logo_id != null
                ? this.apiService.getLogo(link.logo_id).catch((err) => {
                    console.warn('[StudyCreatorMap] getLogo FAILED for logo_id=', link.logo_id, err);
                    return null;
                })
                : Promise.resolve(null),
        ]);
        console.log('[StudyCreatorMap] resolveBucket', link.network_id, 'pois=', pois.length, 'logo=', logo);
        // Inline the logo as a base64 data URL: external image refs inside an SVG
        // loaded from a data: URL are blocked by the browser, so the logo would
        // never render otherwise.
        const logoDataUrl = logo?.link ? await fetchAsDataUrl(logo.link) : null;
        console.log('[StudyCreatorMap] resolveBucket', link.network_id, 'logoDataUrl?', logoDataUrl ? 'OK' : 'NULL');
        return {
            networkId: link.network_id,
            networkName: link.network_name ?? '',
            role: link.role,
            logoUrl: logoDataUrl,
            pois,
        };
    }
    get otherStudies() {
        if (!this.studyPoi)
            return this.ownPois;
        return this.ownPois.filter((p) => p.id !== this.studyPoi.id);
    }
    handleMapLoaded(event) {
        this.mapInstance = event.detail.map;
        this.mapLib = event.detail.mapLib;
        this.renderAllLayers();
        this.renderCurrentMarker();
        // Click on empty map to place the study POI (only when editable and no
        // current marker yet)
        this.mapInstance.on('click', (e) => {
            if (!this.draggable)
                return;
            if (this.currentMarker)
                return;
            this.dispatchEvent(new CustomEvent('study-location-changed', {
                detail: { latitude: e.lngLat.lat, longitude: e.lngLat.lng },
                bubbles: true,
                composed: true,
            }));
        });
        this.dispatchEvent(new CustomEvent('map-loaded', { detail: event.detail }));
    }
    // ─── Current study marker (incremental) ────────────────────────
    renderCurrentMarker() {
        console.log('[StudyCreatorMap] renderCurrentMarker studyPoi=', this.studyPoi, 'hasMarker=', !!this.currentMarker);
        if (!this.mapInstance || !this.mapLib) {
            console.log('[StudyCreatorMap] map not ready, skipping');
            return;
        }
        const hasCoords = this.studyPoi != null &&
            this.studyPoi.latitude != null &&
            this.studyPoi.longitude != null;
        if (!hasCoords) {
            console.log('[StudyCreatorMap] no coords, removing marker');
            if (this.currentMarker) {
                this.currentMarker.remove();
                this.currentMarker = null;
                this.currentMarkerEl = null;
            }
            this.hasFlownToCurrent = false;
            return;
        }
        const lng = this.studyPoi.longitude;
        const lat = this.studyPoi.latitude;
        if (!this.currentMarker) {
            const el = document.createElement('div');
            el.style.width = '34px';
            el.style.height = '34px';
            el.style.cursor = this.draggable ? 'grab' : 'default';
            el.style.zIndex = '1000';
            el.innerHTML = buildPinSvg(COLOR_CURRENT, null);
            const svg = el.firstElementChild;
            if (svg) {
                svg.setAttribute('width', '34');
                svg.setAttribute('height', '34');
            }
            this.currentMarkerEl = el;
            this.currentMarker = new this.mapLib.Marker({
                element: el,
                draggable: this.draggable,
                anchor: 'bottom',
            })
                .setLngLat([lng, lat])
                .addTo(this.mapInstance);
            this.currentMarker.on('dragend', () => {
                const lngLat = this.currentMarker.getLngLat();
                this.dispatchEvent(new CustomEvent('study-location-changed', {
                    detail: { latitude: lngLat.lat, longitude: lngLat.lng },
                    bubbles: true,
                    composed: true,
                }));
            });
        }
        else {
            this.currentMarker.setLngLat([lng, lat]);
        }
        this.applyVisibility();
        if (!this.hasFlownToCurrent) {
            this.mapInstance.flyTo({ center: [lng, lat], zoom: 13 });
            this.hasFlownToCurrent = true;
        }
    }
    // ─── Background / others layers ────────────────────────────────
    renderAllLayers() {
        if (!this.mapInstance)
            return;
        this.clearLayers();
        if (this.otherStudies.length > 0) {
            this.addPoiLayer('others-layer', 'others-source', 'others-pin', COLOR_OTHERS, null, this.otherStudies, 'others', (poi) => this.dispatchPoiClick('others', poi));
        }
        this.attractors.forEach((bucket) => {
            this.addPoiLayer(`attractor-${bucket.networkId}-layer`, `attractor-${bucket.networkId}-source`, `attractor-${bucket.networkId}-pin`, COLOR_ATTRACTOR, bucket.logoUrl, bucket.pois, 'attractors', (poi) => this.openInfoPopup(poi, bucket.networkName, 'attractor'));
        });
        this.competitors.forEach((bucket) => {
            this.addPoiLayer(`competitor-${bucket.networkId}-layer`, `competitor-${bucket.networkId}-source`, `competitor-${bucket.networkId}-pin`, COLOR_COMPETITOR, bucket.logoUrl, bucket.pois, 'competitors', (poi) => this.openInfoPopup(poi, bucket.networkName, 'competitor'));
        });
        this.applyVisibility();
    }
    clearLayers() {
        this.addedLayerIds.forEach((id) => {
            if (this.mapInstance.getLayer(id))
                this.mapInstance.removeLayer(id);
        });
        this.addedSourceIds.forEach((id) => {
            if (this.mapInstance.getSource(id))
                this.mapInstance.removeSource(id);
        });
        this.addedImageIds.forEach((id) => {
            if (this.mapInstance.hasImage(id))
                this.mapInstance.removeImage(id);
        });
        this.addedLayerIds.clear();
        this.addedSourceIds.clear();
        this.addedImageIds.clear();
        this.layerKindByLayerId.clear();
    }
    addPoiLayer(layerId, sourceId, imageId, color, logoUrl, pois, kind, onClick) {
        const validPois = pois.filter((p) => p.latitude != null && p.longitude != null);
        if (validPois.length === 0)
            return;
        const svg = buildPinSvg(color, logoUrl);
        const pinImage = new Image();
        pinImage.src =
            'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
        pinImage.onload = () => {
            if (!this.mapInstance)
                return;
            if (this.mapInstance.hasImage(imageId)) {
                this.mapInstance.removeImage(imageId);
            }
            this.mapInstance.addImage(imageId, pinImage, { sdf: false });
            this.addedImageIds.add(imageId);
            this.mapInstance.addSource(sourceId, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: validPois.map((poi) => ({
                        type: 'Feature',
                        properties: {
                            poi_id: poi.poi_id,
                            poi_uuid: poi.id,
                            name: poi.name,
                        },
                        geometry: {
                            type: 'Point',
                            coordinates: [poi.longitude, poi.latitude],
                        },
                    })),
                },
            });
            this.addedSourceIds.add(sourceId);
            const iconSize = kind === 'others' ? 0.24 : 0.2;
            this.mapInstance.addLayer({
                id: layerId,
                type: 'symbol',
                source: sourceId,
                layout: {
                    'icon-image': imageId,
                    'icon-size': iconSize,
                    'icon-anchor': 'bottom',
                    'icon-allow-overlap': true,
                    visibility: this.visibility[kind] ? 'visible' : 'none',
                },
            });
            this.addedLayerIds.add(layerId);
            this.layerKindByLayerId.set(layerId, kind);
            this.mapInstance.on('click', layerId, (e) => {
                const feature = e.features[0];
                const poi = validPois.find((p) => String(p.poi_id) === String(feature.properties.poi_id));
                if (poi)
                    onClick(poi);
            });
            this.mapInstance.on('mouseenter', layerId, () => {
                this.mapInstance.getCanvas().style.cursor = 'pointer';
            });
            this.mapInstance.on('mouseleave', layerId, () => {
                this.mapInstance.getCanvas().style.cursor = '';
            });
        };
    }
    openInfoPopup(poi, networkName, role) {
        const popupHtml = `
      <div style="max-width: 220px;">
        <div style="font-weight: 600; font-size: 14px; color: #111; margin-bottom: 4px;">${poi.name}</div>
        <div style="font-size: 12px; color: #555;">${networkName}</div>
        <div style="font-size: 11px; color: #888; text-transform: uppercase; margin-top: 2px;">${role}</div>
      </div>
    `;
        new this.mapLib.Popup({
            offset: 25,
            maxWidth: '260px',
            closeButton: true,
            closeOnClick: true,
        })
            .setLngLat([poi.longitude, poi.latitude])
            .setHTML(popupHtml)
            .addTo(this.mapInstance);
    }
    dispatchPoiClick(kind, poi) {
        this.dispatchEvent(new CustomEvent('background-poi-clicked', {
            detail: { kind, poi },
            bubbles: true,
            composed: true,
        }));
    }
    // ─── Visibility ─────────────────────────────────────────────────
    applyVisibility() {
        if (!this.mapInstance)
            return;
        this.layerKindByLayerId.forEach((kind, layerId) => {
            if (this.mapInstance.getLayer(layerId)) {
                this.mapInstance.setLayoutProperty(layerId, 'visibility', this.visibility[kind] ? 'visible' : 'none');
            }
        });
        if (this.currentMarkerEl) {
            this.currentMarkerEl.style.display = this.visibility.current
                ? ''
                : 'none';
        }
    }
    toggleLayer(kind) {
        if (kind === 'current')
            return; // current study is always visible
        this.visibility = { ...this.visibility, [kind]: !this.visibility[kind] };
    }
    layerCount(kind) {
        switch (kind) {
            case 'current':
                return this.studyPoi &&
                    this.studyPoi.latitude != null &&
                    this.studyPoi.longitude != null
                    ? 1
                    : 0;
            case 'others':
                return this.otherStudies.length;
            case 'attractors':
                return this.attractors.reduce((s, b) => s + b.pois.length, 0);
            case 'competitors':
                return this.competitors.reduce((s, b) => s + b.pois.length, 0);
        }
    }
    // ─── Map controls (fullscreen + layers) ────────────────────────
    requestFullscreenToggle() {
        this.dispatchEvent(new CustomEvent('toggle-fullscreen', { bubbles: true, composed: true }));
    }
    renderFullscreenButton() {
        return html `
      <button
        class="ctrl-btn ${this.fullscreen ? 'active' : ''}"
        title=${this.fullscreen ? msg('Exit fullscreen', { id: 'studyMap.exitFullscreen', desc: 'Button title to exit fullscreen map' }) : msg('Fullscreen map', { id: 'studyMap.fullscreen', desc: 'Button title to enter fullscreen map' })}
        @click=${this.requestFullscreenToggle}
      >
        ${this.fullscreen
            ? html `<svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="4 14 10 14 10 20"></polyline>
              <polyline points="20 10 14 10 14 4"></polyline>
              <line x1="14" y1="10" x2="21" y2="3"></line>
              <line x1="3" y1="21" x2="10" y2="14"></line>
            </svg>`
            : html `<svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="15 3 21 3 21 9"></polyline>
              <polyline points="9 21 3 21 3 15"></polyline>
              <line x1="21" y1="3" x2="14" y2="10"></line>
              <line x1="3" y1="21" x2="10" y2="14"></line>
            </svg>`}
      </button>
    `;
    }
    renderLayerControl() {
        const kinds = [
            'current',
            'others',
            'attractors',
            'competitors',
        ];
        return html `
      <button
        class="ctrl-btn ${this.layerMenuOpen ? 'active' : ''}"
        title=${msg('Layers', { id: 'studyMap.layers', desc: 'Layers control button title' })}
        @click=${() => (this.layerMenuOpen = !this.layerMenuOpen)}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
          <polyline points="2 17 12 22 22 17"></polyline>
          <polyline points="2 12 12 17 22 12"></polyline>
        </svg>
      </button>
      ${this.layerMenuOpen
            ? html `
            <div class="layer-menu">
              <div class="layer-menu-title">${msg('Layers', { id: 'studyMap.layers', desc: 'Layers control button title' })}</div>
              ${kinds.map((kind) => {
                const locked = kind === 'current';
                return html `
                  <label class="layer-item ${locked ? 'locked' : ''}">
                    <span
                      class="layer-swatch"
                      style="background: ${LAYER_COLORS[kind]}"
                    ></span>
                    <span class="label">${LAYER_LABELS()[kind]}</span>
                    <span class="count">${this.layerCount(kind)}</span>
                    ${locked
                    ? html `<svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#888"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <rect
                            x="4"
                            y="11"
                            width="16"
                            height="10"
                            rx="2"
                          ></rect>
                          <path d="M8 11V7a4 4 0 0 1 8 0v4"></path>
                        </svg>`
                    : html `
                          <span class="toggle">
                            <input
                              type="checkbox"
                              ?checked=${this.visibility[kind]}
                              @change=${() => this.toggleLayer(kind)}
                            />
                            <span class="track"></span>
                            <span class="knob"></span>
                          </span>
                        `}
                  </label>
                `;
            })}
            </div>
          `
            : ''}
    `;
    }
    // ─── Zone overlay (computed | intersected) ────────────────────
    /**
     * Reconcile Mapbox layers with the current `zones` + `zoneOverlay`:
     *  - zoneOverlay undefined or no zones → remove all zone layers
     *  - zoneOverlay changes → rebuild layers for the new mode
     *  - zones array changes but same mode → incremental diff (add/remove/
     *    color-update) to keep drag/paint reflows minimal.
     */
    async syncZoneOverlay() {
        if (!this.mapInstance)
            return;
        if (!this.zoneOverlay || !this.zones?.length) {
            this.clearZoneLayers();
            this.currentOverlayMode = undefined;
            return;
        }
        // When the mode swaps (computed ↔ intersected), the *source geometry* is
        // different (zone.geojson vs API-fetched geounit FeatureCollection). The
        // cheapest & safest path is to wipe and rebuild — otherwise the old
        // source data stays visible and the user sees no change on toggle.
        if (this.currentOverlayMode &&
            this.currentOverlayMode !== this.zoneOverlay) {
            this.clearZoneLayers();
        }
        this.currentOverlayMode = this.zoneOverlay;
        const desiredZoneIds = new Set(this.zones.map((z) => z.zone_id));
        // 1. Drop layers for zones that are no longer present.
        for (const zoneId of Array.from(this.zoneFillLayerIds.keys())) {
            if (!desiredZoneIds.has(zoneId)) {
                this.removeZoneLayer(zoneId);
            }
        }
        // 2. For each zone, add the layer if missing; otherwise diff color.
        for (const zone of this.zones) {
            const color = this.getZoneColor(zone);
            const existing = this.zoneFillLayerIds.has(zone.zone_id);
            if (!existing) {
                await this.addZoneLayer(zone);
                continue;
            }
            // Existing layer — color-only update (live feedback from ColorPicker).
            const prevColor = this.zoneColorByZoneId.get(zone.zone_id);
            if (prevColor !== color) {
                this.applyZoneColor(zone.zone_id, color);
            }
        }
        // After layers are in place, enforce visibility and fit the viewport
        // to the union of visible geometries.
        this.applyZoneVisibility();
        this.fitToZones();
    }
    getZoneColor(zone) {
        const props = zone.properties;
        const c = props?.color;
        return typeof c === 'string' && c.trim() ? c.trim() : DEFAULT_ZONE_COLOR;
    }
    async getOverlayGeoJSON(zone) {
        if (this.zoneOverlay === 'computed') {
            return zone.geojson ?? null;
        }
        // Intersected mode — fetch geounit polygons on demand, cached.
        const cached = this.zoneGeounitsCache.get(zone.zone_id);
        if (cached)
            return cached;
        if (!this.apiService?.getZoneGeounitsGeojson) {
            console.warn('[StudyCreatorMap] zoneOverlay="intersected" requires apiService.getZoneGeounitsGeojson()');
            return null;
        }
        try {
            const fc = await this.apiService.getZoneGeounitsGeojson(zone.zone_id);
            this.zoneGeounitsCache.set(zone.zone_id, fc);
            return fc;
        }
        catch (e) {
            console.warn('[StudyCreatorMap] failed to fetch zone geounits', zone.zone_id, e);
            return null;
        }
    }
    async addZoneLayer(zone) {
        if (!this.mapInstance)
            return;
        const data = await this.getOverlayGeoJSON(zone);
        if (!data)
            return;
        const color = this.getZoneColor(zone);
        const sourceId = `zone-src-${zone.zone_id}`;
        const fillId = `zone-fill-${zone.zone_id}`;
        const lineId = `zone-line-${zone.zone_id}`;
        // IMPORTANT: zone layers/sources live in their OWN tracking maps
        // (`zoneSourceIds` / `zoneFillLayerIds` / `zoneLineLayerIds`). They must
        // NOT be added to `addedSourceIds` / `addedLayerIds`, because the POI
        // pipeline's `clearLayers()` wipes those sets on every POI re-render
        // (triggered by `studyPoi` changes, which happen on any parent re-render
        // since `livePoi` is a getter returning a new object each time).
        try {
            if (!this.mapInstance.getSource(sourceId)) {
                this.mapInstance.addSource(sourceId, { type: 'geojson', data });
            }
            else {
                this.mapInstance.getSource(sourceId).setData(data);
            }
            if (!this.mapInstance.getLayer(fillId)) {
                this.mapInstance.addLayer({
                    id: fillId,
                    type: 'fill',
                    source: sourceId,
                    paint: {
                        'fill-color': color,
                        'fill-opacity': ZONE_FILL_OPACITY,
                    },
                });
            }
            if (!this.mapInstance.getLayer(lineId)) {
                this.mapInstance.addLayer({
                    id: lineId,
                    type: 'line',
                    source: sourceId,
                    paint: {
                        'line-color': color,
                        'line-width': ZONE_LINE_WIDTH,
                    },
                });
            }
            this.zoneSourceIds.set(zone.zone_id, sourceId);
            this.zoneFillLayerIds.set(zone.zone_id, fillId);
            this.zoneLineLayerIds.set(zone.zone_id, lineId);
            this.zoneColorByZoneId.set(zone.zone_id, color);
        }
        catch (e) {
            console.warn('[StudyCreatorMap] addZoneLayer failed for zone', zone.zone_id, e);
        }
    }
    applyZoneColor(zoneId, color) {
        const fillId = this.zoneFillLayerIds.get(zoneId);
        const lineId = this.zoneLineLayerIds.get(zoneId);
        try {
            if (fillId && this.mapInstance.getLayer(fillId)) {
                this.mapInstance.setPaintProperty(fillId, 'fill-color', color);
            }
            if (lineId && this.mapInstance.getLayer(lineId)) {
                this.mapInstance.setPaintProperty(lineId, 'line-color', color);
            }
            this.zoneColorByZoneId.set(zoneId, color);
        }
        catch (e) {
            console.warn('[StudyCreatorMap] applyZoneColor failed', zoneId, e);
        }
    }
    removeZoneLayer(zoneId) {
        const fillId = this.zoneFillLayerIds.get(zoneId);
        const lineId = this.zoneLineLayerIds.get(zoneId);
        const sourceId = this.zoneSourceIds.get(zoneId);
        try {
            if (fillId && this.mapInstance.getLayer(fillId))
                this.mapInstance.removeLayer(fillId);
            if (lineId && this.mapInstance.getLayer(lineId))
                this.mapInstance.removeLayer(lineId);
            if (sourceId && this.mapInstance.getSource(sourceId)) {
                this.mapInstance.removeSource(sourceId);
            }
        }
        catch (e) {
            console.warn('[StudyCreatorMap] removeZoneLayer failed', zoneId, e);
        }
        if (fillId)
            this.zoneFillLayerIds.delete(zoneId);
        if (lineId)
            this.zoneLineLayerIds.delete(zoneId);
        if (sourceId)
            this.zoneSourceIds.delete(zoneId);
        this.zoneColorByZoneId.delete(zoneId);
    }
    clearZoneLayers() {
        for (const zoneId of Array.from(this.zoneFillLayerIds.keys())) {
            this.removeZoneLayer(zoneId);
        }
        this.lastFitSignature = '';
    }
    /** Toggle visibility of each zone's fill/line layers based on hiddenZoneIds. */
    applyZoneVisibility() {
        if (!this.mapInstance)
            return;
        const hidden = new Set(this.hiddenZoneIds ?? []);
        for (const [zoneId, fillId] of this.zoneFillLayerIds.entries()) {
            const v = hidden.has(zoneId) ? 'none' : 'visible';
            try {
                if (this.mapInstance.getLayer(fillId)) {
                    this.mapInstance.setLayoutProperty(fillId, 'visibility', v);
                }
                const lineId = this.zoneLineLayerIds.get(zoneId);
                if (lineId && this.mapInstance.getLayer(lineId)) {
                    this.mapInstance.setLayoutProperty(lineId, 'visibility', v);
                }
            }
            catch (e) {
                console.warn('[StudyCreatorMap] applyZoneVisibility failed', zoneId, e);
            }
        }
    }
    /** Fit the map viewport to the union of all *visible* zone geometries. */
    fitToZones() {
        if (!this.mapInstance || !this.mapLib)
            return;
        if (!this.zones?.length)
            return;
        const hidden = new Set(this.hiddenZoneIds ?? []);
        const visibleZones = this.zones.filter((z) => !hidden.has(z.zone_id));
        if (!visibleZones.length)
            return;
        const sig = `${this.zoneOverlay}:${visibleZones
            .map((z) => z.zone_id)
            .sort((a, b) => a - b)
            .join(',')}`;
        if (sig === this.lastFitSignature)
            return;
        const bounds = new this.mapLib.LngLatBounds();
        let hasAny = false;
        const extend = (c) => {
            bounds.extend(c);
            hasAny = true;
        };
        for (const z of visibleZones) {
            const geojson = this.zoneOverlay === 'intersected'
                ? this.zoneGeounitsCache.get(z.zone_id)
                : z.geojson;
            if (geojson)
                walkCoords(geojson, extend);
        }
        if (!hasAny)
            return;
        try {
            this.mapInstance.fitBounds(bounds, {
                padding: 40,
                duration: 600,
                maxZoom: 15,
            });
            this.lastFitSignature = sig;
        }
        catch (e) {
            console.warn('[StudyCreatorMap] fitBounds failed', e);
        }
    }
    handleOverlayToggle(e) {
        const value = e.detail.value;
        this.dispatchEvent(new CustomEvent('zone-overlay-change', {
            detail: value,
            bubbles: true,
            composed: true,
        }));
    }
    renderZoneOverlayToggle() {
        if (!this.zoneOverlay || !this.zones?.length)
            return null;
        return html `
      <div class="zone-overlay-bar">
        <mode-input
          .options=${[
            { label: msg('Computed', { id: 'studyMap.overlay.computed', desc: 'Zone overlay mode: computed geometry' }), value: 'computed' },
            { label: msg('Intersected', { id: 'studyMap.overlay.intersected', desc: 'Zone overlay mode: intersected geounit geometry' }), value: 'intersected' },
        ]}
          .value=${this.zoneOverlay}
          size="small"
          @mode-change=${this.handleOverlayToggle}
        ></mode-input>
      </div>
    `;
    }
    render() {
        return html `
      ${this.loading
            ? html `<div class="loader">
            <span class="spinner"></span> ${msg('Loading networks…', { id: 'map.loadingNetworks', desc: 'Loading indicator while networks are being fetched' })}
          </div>`
            : ''}
      <div class="map-controls">
        ${this.renderFullscreenButton()} ${this.renderLayerControl()}
      </div>
      <basic-map
        .mapOptions=${this.mapOptions}
        @map-loaded=${this.handleMapLoaded}
      >
        <slot></slot>
      </basic-map>
      ${this.renderZoneOverlayToggle()}
    `;
    }
};
StudyCreatorMap.styles = css `
    :host {
      display: block;
      position: relative;
      width: 100%;
      height: 100%;
    }
    basic-map {
      display: block;
      width: 100%;
      height: 100%;
    }
    .loader {
      position: absolute;
      top: 12px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(255, 255, 255, 0.95);
      padding: 6px 14px;
      border-radius: 16px;
      font-size: 12px;
      color: #333;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
      z-index: 5;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .spinner {
      width: 12px;
      height: 12px;
      border: 2px solid #ddd;
      border-top-color: #0078d4;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    .map-controls {
      position: absolute;
      top: 12px;
      right: 12px;
      z-index: 6;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 8px;
      font-family: inherit;
    }

    .zone-overlay-bar {
      position: absolute;
      bottom: 14px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 6;
      background: rgba(255, 255, 255, 0.96);
      padding: 6px 8px;
      border-radius: 10px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      border: 1px solid rgba(0, 0, 0, 0.06);
    }

    .ctrl-btn {
      background: #fff;
      border: 1px solid rgba(0, 0, 0, 0.06);
      border-radius: 10px;
      width: 40px;
      height: 40px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #444;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
      transition:
        background 0.12s,
        color 0.12s;
    }
    .ctrl-btn:hover {
      background: #f4f7fb;
      color: #0078d4;
    }
    .ctrl-btn.active {
      background: #0078d4;
      color: #fff;
    }

    .layer-menu {
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
      padding: 6px;
      min-width: 200px;
      animation: menu-pop 0.12s ease-out;
    }
    @keyframes menu-pop {
      from {
        opacity: 0;
        transform: translateY(-4px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .layer-menu-title {
      font-size: 11px;
      font-weight: 600;
      color: #888;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 8px 10px 4px;
    }

    .layer-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 10px;
      cursor: pointer;
      font-size: 13px;
      color: #333;
      user-select: none;
      border-radius: 8px;
    }
    .layer-item:hover {
      background: #f5f8ff;
    }
    .layer-item.disabled {
      opacity: 0.5;
      pointer-events: none;
    }
    .layer-item.locked {
      cursor: default;
    }
    .layer-item.locked:hover {
      background: transparent;
    }

    .layer-swatch {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      flex: 0 0 auto;
      box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.6);
    }
    .layer-item .label {
      flex: 1;
    }
    .layer-item .count {
      color: #888;
      font-size: 11px;
      background: #f0f3f7;
      padding: 1px 6px;
      border-radius: 8px;
    }

    /* Custom toggle switch */
    .toggle {
      position: relative;
      width: 30px;
      height: 16px;
      flex: 0 0 auto;
    }
    .toggle input {
      opacity: 0;
      width: 0;
      height: 0;
      margin: 0;
    }
    .toggle .track {
      position: absolute;
      inset: 0;
      background: #d1d5db;
      border-radius: 16px;
      transition: background 0.18s;
    }
    .toggle .knob {
      position: absolute;
      top: 2px;
      left: 2px;
      width: 12px;
      height: 12px;
      background: #fff;
      border-radius: 50%;
      transition: left 0.18s;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    }
    .toggle input:checked + .track {
      background: #0078d4;
    }
    .toggle input:checked + .track + .knob {
      left: 16px;
    }
  `;
__decorate([
    property({ type: Number, attribute: 'universe-id' })
], StudyCreatorMap.prototype, "universeId", void 0);
__decorate([
    property({ type: Object })
], StudyCreatorMap.prototype, "apiService", void 0);
__decorate([
    property({ type: Object })
], StudyCreatorMap.prototype, "studyPoi", void 0);
__decorate([
    property({ type: Object })
], StudyCreatorMap.prototype, "mapOptions", void 0);
__decorate([
    property({ type: Boolean })
], StudyCreatorMap.prototype, "fullscreen", void 0);
__decorate([
    property({ type: Boolean })
], StudyCreatorMap.prototype, "draggable", void 0);
__decorate([
    property({ type: Array })
], StudyCreatorMap.prototype, "zones", void 0);
__decorate([
    property({ type: String, attribute: 'zone-overlay' })
], StudyCreatorMap.prototype, "zoneOverlay", void 0);
__decorate([
    property({ type: Array })
], StudyCreatorMap.prototype, "hiddenZoneIds", void 0);
__decorate([
    state()
], StudyCreatorMap.prototype, "loading", void 0);
__decorate([
    state()
], StudyCreatorMap.prototype, "ownPois", void 0);
__decorate([
    state()
], StudyCreatorMap.prototype, "attractors", void 0);
__decorate([
    state()
], StudyCreatorMap.prototype, "competitors", void 0);
__decorate([
    state()
], StudyCreatorMap.prototype, "visibility", void 0);
__decorate([
    state()
], StudyCreatorMap.prototype, "layerMenuOpen", void 0);
StudyCreatorMap = __decorate([
    localized(),
    customElement('study-creator-map')
], StudyCreatorMap);
export { StudyCreatorMap };
//# sourceMappingURL=StudyCreatorMap.js.map